import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, Button, Stack } from "@mui/material";
import { Paperclip, Download, X, FileQuestion } from "lucide-react";
import { useSnackbar } from "notistack";
import { ticketService } from "../api/services/ticketService";
import { Loader } from "./Common";
import { downloadBlob, formatFileSize } from "../utils/downloadBlob";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const TEXT_EXTENSIONS = ["txt", "csv", "log", "json", "md"];

function previewKind(contentType, fileName) {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (contentType?.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (contentType === "application/pdf" || ext === "pdf") return "pdf";
  if (contentType?.startsWith("text/") || TEXT_EXTENSIONS.includes(ext)) return "text";
  return "unsupported";
}

/**
 * In-app viewer for a ticket attachment -- images and PDFs render inline
 * (PDF via the browser's own viewer inside an iframe), small text files
 * render as plain text, anything else (Office docs, zips, etc.) falls back
 * to a "no preview available" message with a Download action, since there's
 * no in-browser renderer for those without a third-party viewer.
 */
export default function AttachmentPreviewDialog({ ticketId, attachment, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [objectUrl, setObjectUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const kind = attachment ? previewKind(attachment.content_type, attachment.file_name) : null;

  useEffect(() => {
    if (!attachment) return undefined;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(false);
    setTextContent(null);
    setObjectUrl(null);

    (async () => {
      try {
        if (attachment.has_file) {
          const blob = await ticketService.downloadAttachment(ticketId, attachment.id);
          if (cancelled) return;
          const kindForBlob = previewKind(attachment.content_type, attachment.file_name);
          if (kindForBlob === "text") {
            setTextContent(await blob.text());
          } else {
            createdUrl = URL.createObjectURL(blob);
            setObjectUrl(createdUrl);
          }
        } else if (attachment.file_url) {
          // Legacy manually-entered URL, not a file we control -- point the
          // viewer straight at it rather than fetching (avoids CORS issues).
          setObjectUrl(attachment.file_url);
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment, ticketId]);

  const handleDownload = async () => {
    if (!attachment) return;
    if (attachment.has_file) {
      try {
        const blob = await ticketService.downloadAttachment(ticketId, attachment.id);
        downloadBlob(blob, attachment.file_name);
      } catch {
        enqueueSnackbar("Couldn't download file", { variant: "error" });
      }
    } else if (attachment.file_url) {
      window.open(attachment.file_url, "_blank", "noreferrer");
    }
  };

  return (
    <Dialog open={Boolean(attachment)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Paperclip size={18} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{attachment?.file_name}</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ minHeight: 420, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fafafa", p: kind === "text" ? 0 : 2 }}
      >
        {loading && <Loader label="Loading preview..." />}

        {!loading && error && (
          <Stack spacing={1.5} alignItems="center">
            <FileQuestion size={32} />
            <Typography color="text.secondary">Couldn't load a preview for this file.</Typography>
          </Stack>
        )}

        {!loading && !error && kind === "image" && (
          <Box component="img" src={objectUrl} alt={attachment.file_name} sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
        )}

        {!loading && !error && kind === "pdf" && (
          <Box component="iframe" src={objectUrl} title={attachment.file_name} sx={{ width: "100%", height: "70vh", border: "none", bgcolor: "#fff" }} />
        )}

        {!loading && !error && kind === "text" && (
          <Box
            component="pre"
            sx={{ width: "100%", maxHeight: "70vh", overflow: "auto", m: 0, p: 2, bgcolor: "#fff", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {textContent}
          </Box>
        )}

        {!loading && !error && kind === "unsupported" && (
          <Stack spacing={1.5} alignItems="center">
            <Paperclip size={32} />
            <Typography color="text.secondary">Preview isn't available for this file type -- download it instead.</Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {attachment?.file_size != null && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>{formatFileSize(attachment.file_size)}</Typography>
        )}
        <Button startIcon={<Download size={16} />} onClick={handleDownload}>Download</Button>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
